import React, { useEffect, useRef, useState } from 'react'

import PropTypes from 'prop-types'

import {
  AutoCompleteFilterInput,
  BooleanFilterInput,
  DateTimePickerFilterInput,
  SearchFieldFilterInput,
  SelectFilterInput,
} from './inputs'

import filters from '../../config/filters.json'
import { saveSessionFilters } from '../../useSessionFilters'

import reportApi from '../../api/table'
import useHandleError from '../../api/useHandleError'
import { Root, TableFilterInputWrapper, TableFilterWideInputWrapper } from '../../styles/components/TableFiltersStyle'

const TableFilters = ({ setFilters, page = 'table', usedFilters = {} }) => {
  const [selectedValues, setSelectedValues] = useState(usedFilters)
  const [renderedFilters, setRenderedFilters] = useState([])
  const [filterOptions, setFilterOptions] = useState(null)
  const handleError = useHandleError()

  /* Handle value of filter input */
  const handleSelectedValues = (e, selectedObject, reason) => {
    let newSelectedValue = {}

    if (selectedObject) {
      /* Passed whole object as new filter with value,
      in case of invalid event (without name or value) */
      newSelectedValue = selectedObject
    } else {
      /* Passed only event, so we have to create filter with value */
      const { name, value } = e.target
      newSelectedValue = { [name]: value }
    }

    const selectedFilterValue = Object.values(newSelectedValue)[0]
    const filterNameToBeCleared = Object.keys(newSelectedValue)[0]

    const selectedFilters = {
      ...selectedValues,
      ...newSelectedValue,
    }

    if (reason === 'clear' || typeof selectedFilterValue === 'undefined' || selectedFilterValue === '') {
      const clearedFilters = { ...selectedValues }
      delete clearedFilters[filterNameToBeCleared]
      page === 'table' && saveSessionFilters(selectedFilters)
      return setSelectedValues(clearedFilters)
    }

    /* Append new filter selected value to others */
    setSelectedValues(selectedFilters)

    page === 'table' && saveSessionFilters(selectedFilters)
  }

  const getFilterOptions = () =>
    reportApi
      .filters()
      .then((data) => data.data)
      .catch(handleError)

  const filtersWithValues = usedFilters ? filters.map((item, i) => Object.assign({}, item, usedFilters[i])) : filters

  /* Measure impact with large data */
  const getRenderedFilters = (filterValues) => {
    /* Render filters from database */
    const filterFields = filtersWithValues.map((filter, index) => {
      /* If filter shouldn't appear on defined page */
      if (!filter.pages.includes(page)) {
        return []
      }

      const FilterWrapper = filter.size === 'wide' ? TableFilterWideInputWrapper : TableFilterInputWrapper
      let Field = null
      switch (filter.type) {
        case 'datetime':
          Field = (
            <DateTimePickerFilterInput
              key={index}
              filter={filter}
              options={filterValues}
              selectedValues={selectedValues}
              handleSelectedValues={handleSelectedValues}
              isSetToMidnight={filter.field === 'date_to'}
              sx={{ width: '100%' }}
            />
          )
          break

        case 'autocomplete':
          Field = (
            <AutoCompleteFilterInput
              key={index}
              filter={filter}
              options={filterValues}
              selectedValues={selectedValues}
              handleSelectedValues={handleSelectedValues}
              aria-label={`${filter.field}-autocomplete-filter`}
              sx={{ width: '100%' }}
            />
          )
          break

        case 'select':
          Field = (
            <SelectFilterInput
              key={index}
              filter={filter}
              options={filterValues}
              selectedValues={selectedValues}
              handleSelectedValues={handleSelectedValues}
              aria-label={`${filter.field}-select-filter`}
              sx={{ width: '100%' }}
            />
          )
          break

        case 'boolean':
          Field = (
            <BooleanFilterInput
              key={index}
              filter={filter}
              selectedValues={selectedValues}
              handleSelectedValues={handleSelectedValues}
              sx={{ width: '100%' }}
            />
          )
          break

        case 'searchField':
          Field = (
            <SearchFieldFilterInput
              key={index}
              filter={filter}
              selectedValues={selectedValues}
              handleSelectedValues={handleSelectedValues}
              sx={{ width: '100%' }}
            />
          )
          break
      }
      return <FilterWrapper key={index}>{Field}</FilterWrapper>
    })

    return filterFields
  }

  const isInitialMount = useRef(true)
  useEffect(() => {
    const updateFilters = async () => {
      const usedFilterOptions = filterOptions ? filterOptions : await getFilterOptions()
      if (isInitialMount.current) {
        setFilterOptions(usedFilterOptions)
      }
      isInitialMount.current = false
      setRenderedFilters(getRenderedFilters(usedFilterOptions))
      setFilters(selectedValues)
    }
    updateFilters()
  }, [selectedValues])

  useEffect(() => {
    const isClearedSelectedValues = Object.keys(usedFilters).length === 0

    if (isClearedSelectedValues && usedFilters !== selectedValues && !isInitialMount.current) {
      setSelectedValues({})
    }
  }, [usedFilters])

  return <Root className={`sticky-vertical ${page === 'table' && 'viewportWidth'}`}>{renderedFilters}</Root>
}

TableFilters.propTypes = {
  rows: PropTypes.array,
  setFilters: PropTypes.func,
  page: PropTypes.string,
  usedFilters: PropTypes.object,
}

export default TableFilters
